<script setup lang="ts">
type ButtonProps = {
  as?: 'button' | 'a' | 'router-link'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  variant?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'outline'
    | 'success_yes'
    | 'neutral'
  isRounded?: boolean
  isWide?: boolean
  isLoading?: boolean
}

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()

const props = withDefaults(defineProps<ButtonProps>(), {
  as: 'button',
})

const isDisabled = computed(
  () =>
    typeof attrs.disabled === 'string' || !!attrs.disabled || props.isLoading,
)
</script>

<template>
  <component
    :is="as"
    :class="[
      'items-center justify-center gap-2 border font-medium focus:outline-none focus:ring-offset-2',
      isWide ? 'flex w-full' : 'inline-flex',
      props.variant && 'shadow-sm',
      props.as === 'button' &&
        'disabled:cursor-not-allowed disabled:opacity-60',
      props.as !== 'button' && isDisabled && 'pointer-events-none opacity-60',
      {
        // Size
        'px-2.5 py-1.5 text-xs': props.size === 'xs',
        'px-3 py-2 text-sm leading-4': props.size === 'sm',
        'px-4 py-2.5': props.size === 'md',
        'px-4 py-2 text-base': props.size === 'lg',
        'px-6 py-3 text-base': props.size === 'xl',
      },
      {
        'rounded-full': props.isRounded,
      },
      {
        // Variants
        'border-transparent bg-brand-primary text-white hover:bg-primary-700 focus:ring-2 focus:ring-primary-600':
          props.variant === 'primary',
        'border-transparent bg-brand-neutral text-slate-800 focus:ring-2 focus:ring-slate-800':
          props.variant === 'secondary',
        'border-transparent bg-brand-success text-white hover:bg-emerald-600 focus:ring-2 focus:ring-emerald-500':
          props.variant === 'success',
        'focus:ring-success_yes border-transparent bg-brand-success_yes text-brand-black hover:bg-emerald-600 focus:ring-2':
          props.variant === 'success_yes',
        'border-transparent bg-brand-neutral text-brand-black hover:bg-emerald-600 focus:ring-2 focus:ring-brand-neutral':
          props.variant === 'neutral',
        'border-transparent bg-brand-danger text-white':
          props.variant === 'error',
        'border border-brand-border bg-transparent':
          props.variant === 'outline',
      },
    ]"
    v-bind="$attrs"
    :disabled="props.as === 'button' ? isDisabled : undefined"
  >
    <AppiconLoader v-if="isLoading" class="h-4 w-4 animate-spin text-white" />
    <slot />
  </component>
</template>
